import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { FiTruck, FiCheckCircle, FiAlertCircle, FiPackage } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const GoodsReceiptRegisterReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("GrnRegister");
  const [searchTerm, setSearchTerm] = useState("");
  const [resultFilter, setResultFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalDeliveries = data.length;
    const acceptedCount = data.filter((r) => r.inspectionResult === "Accepted").length;
    const rejectedCount = data.filter((r) => r.inspectionResult === "Rejected / Returned").length;
    const uniqueVendors = new Set(data.map((r) => r.vendorName)).size;

    return {
      totalDeliveries,
      acceptedCount,
      rejectedCount,
      uniqueVendors,
    };
  }, [data]);

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "GRN Number",
        field: "grnNo",
        colId: "grnNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              color: "var(--color-primary, #0b6462)",
              fontFamily: "monospace",
              fontSize: "12px",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Date of Delivery",
        field: "deliveryDate",
        colId: "deliveryDate",
        flex: 1.1,
        minWidth: 125,
        valueFormatter: (p) => (p.value ? moment(p.value).format("DD-MMM-YYYY") : "—"),
      },
      {
        headerName: "Project / Store Location",
        field: "location",
        colId: "location",
        flex: 1.5,
        minWidth: 180,
      },
      {
        headerName: "Supplier / Vendor Name",
        field: "vendorName",
        colId: "vendorName",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Linked PO No.",
        field: "poNumber",
        colId: "poNumber",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => (
          <span
            style={{
              fontFamily: "monospace",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              padding: "1px 6px",
              borderRadius: "4px",
              fontSize: "11px",
              color: "#475569",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Vehicle / Bilty No. & Driver Name",
        field: "vehicleBilty",
        colId: "vehicleBilty",
        flex: 1.8,
        minWidth: 210,
      },
      {
        headerName: "Material Name, Received Qty & UOM",
        field: "materialDetail",
        colId: "materialDetail",
        flex: 2,
        minWidth: 230,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</span>,
      },
      {
        headerName: "Quality Inspection Result",
        field: "inspectionResult",
        colId: "inspectionResult",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => {
          const val = p.value || "Accepted";
          const type = val === "Accepted" ? "success" : "danger";
          return <StatusChip status={val} type={type} />;
        },
      },
      {
        headerName: "Receiver / Storekeeper Name",
        field: "receiverName",
        colId: "receiverName",
        flex: 1.5,
        minWidth: 180,
      },
    ];
  }, []);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
    }),
    [],
  );

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.grnNo && item.grnNo.toLowerCase().includes(q)) ||
        (item.location && item.location.toLowerCase().includes(q)) ||
        (item.vendorName && item.vendorName.toLowerCase().includes(q)) ||
        (item.poNumber && item.poNumber.toLowerCase().includes(q)) ||
        (item.materialDetail && item.materialDetail.toLowerCase().includes(q)) ||
        (item.vehicleBilty && item.vehicleBilty.toLowerCase().includes(q));

      const matchesResult =
        resultFilter === "all" ||
        item.inspectionResult?.toLowerCase() === resultFilter.toLowerCase();

      return matchesSearch && matchesResult;
    });
  }, [data, searchTerm, resultFilter]);

  const onGridReady = useCallback((params) => {
    const map = {};
    params.api.getColumns()?.forEach((col) => {
      if (col.getColDef().headerName) {
        map[col.getColId()] = col.isVisible();
      }
    });
    setColumnVisibility(map);
  }, []);

  const toggleColumn = useCallback(
    (colId) => {
      if (!gridRef.current?.api) {
        return;
      }
      const isCurrentlyVisible = columnVisibility[colId];
      gridRef.current.api.setColumnsVisible([colId], !isCurrentlyVisible);
      setColumnVisibility((prev) => ({ ...prev, [colId]: !isCurrentlyVisible }));
    },
    [columnVisibility],
  );

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Goods_Receipt_Note_Register_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("GRN register exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL DELIVERIES RECEIVED"
            value={`${kpis.totalDeliveries} GRNs`}
            subtitle="Inward shipments recorded"
            icon={FiTruck}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ACCEPTED & PASSED QA"
            value={`${kpis.acceptedCount} GRNs`}
            subtitle="Approved for stock booking"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="REJECTED / RETURNED"
            value={`${kpis.rejectedCount} GRN(s)`}
            subtitle="Failed site QA / transit damage"
            icon={FiAlertCircle}
            color="#dc2626"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="ACTIVE SUPPLYING VENDORS"
            value={`${kpis.uniqueVendors} Vendors`}
            subtitle="Delivered across all sites"
            icon={FiPackage}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (General Journal style) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="GRN Register Report"
            >
              GRN Register Report
            </div>
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search GRN, PO, vendor, item..."
            />

            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ maxWidth: "240px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={resultFilter}
                  onChange={(e) => setResultFilter(e.target.value)}
                >
                  <option value="all">All Inspection Results</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected / returned">Rejected / Returned</option>
                </select>
                <label>Inspection QA</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              tooltipShowDelay={0}
              onGridReady={onGridReady}
              rowStyle={{ cursor: "pointer" }}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoodsReceiptRegisterReport;
